import axios, { isAxiosError, type AxiosRequestConfig } from 'axios';
import type { OwmCurrent } from '../lib/openweather';
import type { Domain, ScreenId, SnapshotResponse, TicketResponse } from '../protocol/types';

/** Unico punto del progetto che parla con le API REST. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/v1',
  timeout: 8000,
  headers: { 'Content-Type': 'application/json' },
});

async function call<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    return (await http.request<T>(config)).data;
  } catch (e) {
    if (isAxiosError(e)) throw new ApiError(e.response?.status ?? 0, e.message);
    throw e;
  }
}

/** Scambia la device key del kiosk con un ticket monouso per il WebSocket. */
export function getTicket(deviceKey: string, screen: ScreenId): Promise<TicketResponse> {
  return call<TicketResponse>({
    method: 'POST',
    url: '/display/ticket',
    data: { deviceKey, screen },
  });
}

/** Ripiego quando il WebSocket non è raggiungibile (proxy aziendali, firewall). */
export function getSnapshot(deviceKey: string, domains: Domain[]): Promise<SnapshotResponse> {
  return call<SnapshotResponse>({
    method: 'GET',
    url: `/display/snapshot?domains=${encodeURIComponent(domains.join(','))}`,
    headers: { Authorization: `Device ${deviceKey}` },
  });
}

const weatherHttp = axios.create({ baseURL: '/owm', timeout: 5000 });

/**
 * Meteo attuale da OpenWeather, finché non arriva dal backend. L'app non ha la chiave: chiama
 * /owm/weather e il server (Vite in sviluppo, nginx in produzione) la aggiunge prima di inoltrare.
 */
export async function getCurrentWeather(lat: number, lon: number): Promise<OwmCurrent> {
  try {
    const res = await weatherHttp.get<OwmCurrent>('/weather', {
      params: { lat, lon, units: 'metric', lang: 'it' },
    });
    return res.data;
  } catch (e) {
    throw new ApiError(isAxiosError(e) ? (e.response?.status ?? 0) : 0, 'meteo non disponibile');
  }
}

/** Legge la device key dal fragment dell'URL (#k=…), con ripiego su localStorage. */
export function readDeviceKey(): string | null {
  const fromHash = new URLSearchParams(window.location.hash.slice(1)).get('k');
  try {
    if (fromHash) {
      localStorage.setItem('tiw.deviceKey', fromHash);
      return fromHash;
    }
    return localStorage.getItem('tiw.deviceKey');
  } catch {
    return fromHash;
  }
}
